import BaseApiService from './BaseApiService.js';

class TeacherService extends BaseApiService {

  // ✅ Teacher Registration (Admin only - requires Authorization header)
  async createTeacher(teacherData) {
    // Validate required fields
    if (!teacherData.teacherName || !teacherData.teacherName.trim()) {
      throw new Error('Teacher name is required');
    }
    if (!teacherData.teacherId) {
      throw new Error('Teacher ID is required');
    }

    // Clean up payload - keep teacherId as string (can be numeric or string like "T001")
    const cleanedData = {
      ...teacherData,
      teacherName: teacherData.teacherName.trim(),
      teacherId: String(teacherData.teacherId), // Ensure it's a string
      email: teacherData.email || teacherData.gmail || '',
      phone: teacherData.phone || teacherData.phoneNumber || '',
      password: teacherData.password || 'teacher123',
      experience: parseInt(teacherData.experience) || 0,
      photo: teacherData.photo || '',
      photoUrl: teacherData.photoUrl || '',
      status: teacherData.status || 'ACTIVE'
    };

    console.log('📤 Creating teacher with payload:', cleanedData);
    console.log('ℹ️ OrgId will be extracted from authentication token');
    
    try {
      const response = await this.makeRequest('/api/teacher/register', {
        method: 'POST',
        body: JSON.stringify(cleanedData)
      });
      console.log('✅ Teacher created successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Failed to create teacher:', error);
      throw error;
    }
  }

  // ✅ Get all teachers (Admin only)
  async getTeachers() {
    return this.makeRequest('/api/teacher/all', { 
      method: 'GET'
    });
  }

  // ✅ Alias for getTeachers
  async getAllTeachers(orgId) {
    if (orgId) {
      return this.getTeachersByOrg(orgId);
    }
    return this.getTeachers();
  }

  // ✅ Get teachers by organization
  async getTeachersByOrg(orgId) {
    if (!orgId) {
      console.warn('⚠️ OrgId is null or undefined, cannot fetch teachers');
      return [];
    }
    try {
      console.log(`🚀 Fetching teachers for orgId: ${orgId} (Type: ${typeof orgId})`);
      
      // Use organization-specific endpoint to fetch teachers directly
      const response = await this.makeRequest(`/api/admin/college/${orgId}/teachers`, { 
        method: 'GET'
      });
      
      console.log('📥 Raw API response:', response);
      
      // Extract teachers array from response
      let teachersArray = [];
      if (Array.isArray(response)) {
        teachersArray = response;
        console.log(`✅ Response is direct array with ${teachersArray.length} teachers`);
      } else if (response && response.teachers && Array.isArray(response.teachers)) {
        teachersArray = response.teachers;
        console.log(`✅ Response has teachers property with ${teachersArray.length} teachers`);
      } else if (response && Array.isArray(response.data)) {
        teachersArray = response.data;
        console.log(`✅ Response has data property with ${teachersArray.length} teachers`);
      } else if (response && response.success && Array.isArray(response.data)) {
        teachersArray = response.data;
        console.log(`✅ Response has success flag with ${teachersArray.length} teachers`);
      } else {
        console.warn('⚠️ Unexpected response format:', response);
        return [];
      }
      
      console.log(`📊 Fetched ${teachersArray.length} teachers for orgId: ${orgId}`);
      return teachersArray;
    } catch (error) {
      console.error(`❌ Failed to fetch teachers for org ${orgId}:`, error);
      throw error;
    }
  }

  // ✅ Teacher Profile Management (requires Authorization header)
  async getTeacherProfile() {
    return this.makeRequest('/api/teacher/profile', { 
      method: 'GET'
    });
  }

  // ✅ Update teacher profile
  async updateTeacherProfile(teacherData) {
    return this.makeRequest('/api/teacher/profile', {
      method: 'PUT',
      body: JSON.stringify(teacherData)
    });
  }

  // ✅ Teacher Student Access (requires Authorization header)
  async getTeacherStudents() {
    return this.makeRequest('/api/teacher/students', { 
      method: 'GET'
    });
  }

  // ✅ Get students by semester for teacher
  async getTeacherStudentsBySemester(semester) {
    return this.makeRequest(`/api/teacher/students/semester/${semester}`, { 
      method: 'GET'
    });
  }

  // ✅ Get teacher access information
  async getTeacherAccessInfo() {
    return this.makeRequest('/api/teacher/access-info', { 
      method: 'GET'
    });
  }

  // ✅ Admin Teacher Management (requires Authorization header)
  async getTeachersByDepartment(department) {
    return this.makeRequest(`/api/teacher/department/${department}`, { 
      method: 'GET'
    });
  }

  // ✅ Deactivate teacher
  async deactivateTeacher(teacherId) {
    return this.makeRequest(`/api/teacher/deactivate/${teacherId}`, { 
      method: 'PUT'
    });
  }

  // ✅ Reactivate teacher
  async reactivateTeacher(teacherId) {
    return this.makeRequest(`/api/teacher/reactivate/${teacherId}`, { 
      method: 'PUT'
    });
  }

  // ✅ Get teacher by ID
  async getTeacherById(teacherId) {
    return this.makeRequest(`/api/teacher/${teacherId}`, { 
      method: 'GET'
    });
  }

  // ✅ Update teacher information (Admin only)
  async updateTeacher(teacherId, teacherData) {
    if (!teacherId) {
      throw new Error('Teacher ID is required to update teacher information');
    }

    if (!teacherData || !teacherData.teacherName || !teacherData.teacherName.trim()) {
      throw new Error('Teacher name is required');
    }

    // Get orgId from localStorage (stored during login)
    const orgData = localStorage.getItem('orgData');
    let orgId = null;
    if (orgData) {
      try {
        const parsedOrgData = JSON.parse(orgData);
        orgId = parsedOrgData.orgId;
      } catch (error) {
        console.warn('⚠️ Could not parse orgData from localStorage:', error);
      }
    }
    
    if (!orgId) {
      orgId = localStorage.getItem('adminOrgId');
    }

    // Clean up and normalize payload before sending to backend
    const normalizedPhone = (teacherData.phoneNumber || teacherData.phone || '').toString().trim();
    const cleanedData = {
      teacherId: String(teacherId),
      teacherName: teacherData.teacherName.trim(),
      // Email fields - keep both gmail and email for backend compatibility
      gmail: (teacherData.gmail || teacherData.email || '').trim(),
      email: (teacherData.email || teacherData.gmail || '').trim(),
      // Phone fields - keep both phoneNumber and phone in sync
      phoneNumber: normalizedPhone,
      phone: normalizedPhone,
      designation: teacherData.designation || '',
      degree: teacherData.degree || '',
      specialization: teacherData.specialization || '',
      departmentId: teacherData.departmentId || null,
      // Map departmentName to department (backend expects 'department' field)
      department: teacherData.departmentName || teacherData.department || '',
      experience: teacherData.experience ? parseInt(teacherData.experience, 10) || 0 : 0,
      // Include DOB in multiple compatible fields if provided
      ...(teacherData.dob && teacherData.dob.trim()
        ? {
            dob: teacherData.dob.trim(),
            dateOfBirth: teacherData.dob.trim(),
            birthDate: teacherData.dob.trim()
          }
        : {}),
      address: teacherData.address ? teacherData.address.trim() : '',
      qualifications: teacherData.qualifications || '',
      status: teacherData.status || 'ACTIVE',
      subjects: Array.isArray(teacherData.subjects) ? teacherData.subjects : [],
      ...(teacherData.photo !== undefined ? { photo: teacherData.photo } : {}),
      ...(teacherData.photoUrl !== undefined ? { photoUrl: teacherData.photoUrl } : {})
    };

    // Include orgId if available (backend may need it if not in token)
    if (orgId) {
      cleanedData.orgId = orgId;
      console.log(`ℹ️ Including orgId in payload: ${orgId}`);
    } else {
      console.warn('⚠️ orgId not found in localStorage - backend will attempt to extract from token');
    }

    // Include password only if admin explicitly sets it (for reset scenarios)
    if (teacherData.password && teacherData.password.toString().trim()) {
      cleanedData.password = teacherData.password.toString().trim();
    }

    // Remove null/undefined fields so we do not send noisy data
    const finalPayload = Object.entries(cleanedData).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && (value !== '' || key === 'subjects' || key === 'photo' || key === 'photoUrl')) {
        acc[key] = value;
      }
      return acc;
    }, {});
    if (Array.isArray(cleanedData.subjects)) {
      finalPayload.subjects = cleanedData.subjects;
    }

    console.log('📤 Updating teacher with payload:', finalPayload);

    return this.makeRequest(`/api/teacher/update/${teacherId}`, {
      method: 'PUT',
      body: JSON.stringify(finalPayload)
    });
  }

  // ✅ Update or delete teacher profile photo (Admin or Teacher)
  async updateTeacherPhoto(teacherId, photoData) {
    if (!teacherId) throw new Error('Teacher ID is required');
    return this.makeRequest(`/api/teacher/${encodeURIComponent(teacherId)}/photo`, {
      method: 'POST',
      body: JSON.stringify({ photo: photoData || '' })
    });
  }

  // ✅ Search teachers
  async searchTeachers(searchTerm) {
    return this.makeRequest(`/api/teacher/search?q=${encodeURIComponent(searchTerm)}`, { 
      method: 'GET'
    });
  }

  // ✅ Delete teacher (Admin only)
  async deleteTeacher(teacherId) {
    return this.makeRequest(`/api/teacher/delete/${teacherId}`, {
      method: 'DELETE'
    });
  }

  // ==================== TEACHER ATTENDANCE (ADMIN) ====================
  async getTeacherAttendance(teacherId) {
    return this.makeRequest(`/api/admin/teacher/attendance/${teacherId}`, { method: 'GET' });
  }

  async markTeacherAttendance(body) {
    return this.makeRequest('/api/admin/teacher/attendance/mark', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async getTeacherAttendanceByDate(date) {
    return this.makeRequest(`/api/admin/teacher/attendance/by-date?date=${encodeURIComponent(date)}`, { method: 'GET' });
  }

  async getTeacherAttendanceReport(startDate, endDate) {
    return this.makeRequest(`/api/admin/teacher/attendance/report?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`, { method: 'GET' });
  }

  async markBulkTeacherAttendance(bodyList) {
    return this.makeRequest('/api/admin/teacher/attendance/mark-bulk', {
      method: 'POST',
      body: JSON.stringify(bodyList)
    });
  }

  // ==================== TEACHER LEAVES (ADMIN) ====================
  async getTeacherLeaves(teacherId) {
    return this.makeRequest(`/api/admin/teacher/leaves/${teacherId}`, { method: 'GET' });
  }

  async applyTeacherLeave(body) {
    return this.makeRequest('/api/admin/teacher/leaves/apply', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async updateTeacherLeaveStatus(leaveId, status, remarks = '') {
    return this.makeRequest(`/api/admin/teacher/leaves/${leaveId}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, remarks })
    });
  }

  // ==================== TEACHER PAYROLL (ADMIN) ====================
  async getTeacherPayroll(teacherId) {
    return this.makeRequest(`/api/admin/teacher/payroll/${teacherId}`, { method: 'GET' });
  }

  async generateTeacherPayroll(body) {
    return this.makeRequest('/api/admin/teacher/payroll/generate', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  // ==================== STUDENT BULK ATTENDANCE ====================
  async markStudentBulkAttendance(body) {
    return this.makeRequest('/api/teacher/attendance/mark-bulk', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  // Get student attendance marked by the teacher on a specific date
  async getTeacherMarkedStudentAttendance(date) {
    return this.makeRequest(`/api/teacher/attendance/marked?date=${date}`, { method: 'GET' });
  }
}

// ✅ Export single instance
const teacherService = new TeacherService();
export default teacherService;
